import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import type { SeoMeta } from "@/types"
import SocialShareBar from "@/components/public/social-share-bar"

/* ------------------------------------------------------------------ */
/* FAQ                                                                 */
/* ------------------------------------------------------------------ */

const FAQ = [
  {
    q: "Do I need expensive equipment to create property videos?",
    a: "No. With tools like QuiteRight, you can create professional property videos using your existing listing photographs. No camera, no editing software, no filming experience needed. The platform handles scripting, voiceover, music, and rendering automatically.",
  },
  {
    q: "How long should a property video be?",
    a: "For social media (Instagram Reels, TikTok, YouTube Shorts), keep videos between 15-45 seconds. This length is optimised for engagement and shares. For website or portal use, 60-90 seconds works well. Longer is rarely better; attention drops sharply after 60 seconds.",
  },
  {
    q: "Which social media platform is best for property video?",
    a: "Instagram consistently delivers the best engagement for property content in the UK. Instagram Reels reach both followers and new audiences through the algorithm. Facebook is strong for local reach, and TikTok is growing rapidly among younger buyers. The best strategy is to post on all three from a single video.",
  },
  {
    q: "How do I measure the success of property video marketing?",
    a: "Track three metrics: views (reach), enquiries generated (conversion), and time to offer (speed). Compare listings marketed with video against those without. Most agents see a clear uplift in viewings booked within 2-4 weeks of adding video to their marketing mix.",
  },
  {
    q: "How much does property video marketing cost?",
    a: "A professional videographer charges £300-800 per property visit. QuiteRight creates videos from £49/month (20 videos on the Agent plan), working out to about £2.45 per video. Even the Managed plan at £299/month for 50 videos costs just £6 per video, a fraction of traditional videography.",
  },
]

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export default function PropertyVideoMarketingGuide({ meta }: { meta: SeoMeta }) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "Property Video Marketing Guide for Estate Agents",
            description: meta.description,
            publisher: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://www.quiteright.co.uk",
              logo: {
                "@type": "ImageObject",
                url: "https://www.quiteright.co.uk/qr-logo-wordmark-red.png",
              },
            },
            datePublished: "2026-02-22",
            dateModified: "2026-02-22",
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>
        <BreadcrumbSchema canonical={meta.canonical} items={[{ name: "Guides", path: "/guides" }, { name: "Property Video Marketing" }]} />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
            Guide
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            Property video marketing
            <span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            The definitive guide to video marketing for UK estate agents.
            Why video matters, what types work best, which platforms to
            target, and how to get started.
          </p>
        </div>
      </section>

      <SocialShareBar />

      {/* Content */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          {/* Why video */}
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Why video matters for estate agents<span className="text-red">.</span>
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate">
            <p>
              Property video has moved from luxury to necessity. The numbers
              are clear: listings with video receive 2x more enquiries,
              73% of buyers watch property video before booking a viewing,
              and 40% of vendors say an agent&rsquo;s marketing quality
              influenced their choice.
            </p>
            <p>
              Social media algorithms heavily favour video content. An
              Instagram Reel or TikTok of a property can reach thousands of
              local buyers organically, something a static photograph
              simply cannot achieve.
            </p>
            <p>
              For agents, video is both a listing tool and a business
              development tool. It helps sell the property faster, and it
              helps win the instruction in the first place.
            </p>
          </div>

          {/* Types of video */}
          <h2 className="mt-12 font-display text-2xl font-bold text-charcoal">
            Types of property video<span className="text-red">.</span>
          </h2>
          <div className="mt-6 space-y-4">
            {[
              {
                title: "Listing video ads",
                desc: "Short-form (15-45 seconds) videos designed for social media. These combine property photos with music, voiceover, and text overlays to create scroll-stopping ads. The most effective type for generating enquiries.",
              },
              {
                title: "Virtual tours",
                desc: "Longer walkthrough videos (60-120 seconds) that guide the viewer through the property room by room. Best for website embeds and serious buyers who want a detailed look before booking a viewing.",
              },
              {
                title: "Agent introduction videos",
                desc: "Videos where the agent speaks to camera about a property or the local market. These build personal brand and trust. Best for social media profiles and market appraisal presentations.",
              },
              {
                title: "Area guide videos",
                desc: "Videos showcasing the neighbourhood: parks, schools, transport, high street, and lifestyle. These attract buyers searching for information about the area and position you as the local expert.",
              },
              {
                title: "Testimonial videos",
                desc: "Happy vendor and buyer testimonials. Powerful social proof for market appraisals and your agency website. Even a 15-second quote can be very persuasive.",
              },
            ].map((type) => (
              <div
                key={type.title}
                className="rounded-xl border border-stone bg-white p-5"
              >
                <h3 className="font-display text-base font-bold text-charcoal">
                  {type.title}
                </h3>
                <p className="mt-2 text-sm text-slate">{type.desc}</p>
              </div>
            ))}
          </div>

          {/* Platforms */}
          <h2 className="mt-12 font-display text-2xl font-bold text-charcoal">
            Best platforms for property video<span className="text-red">.</span>
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate">
            <p>
              The key platforms for UK estate agents in 2026 are:
            </p>
          </div>
          <div className="mt-4 space-y-4">
            {[
              {
                platform: "Instagram Reels",
                why: "The highest-engagement platform for UK property content. Reels reach non-followers through the algorithm, making it ideal for local lead generation. Post in 9:16 portrait format.",
              },
              {
                platform: "TikTok",
                why: "Growing rapidly among first-time buyers (25-35 age group). The algorithm rewards consistent posting. Even small agencies can go viral with the right content.",
              },
              {
                platform: "Facebook",
                why: "Still the largest platform by UK users. Strong for local targeting through groups and marketplace. Video posts get 6x more engagement than text or photo posts.",
              },
              {
                platform: "YouTube Shorts",
                why: "YouTube is the second-largest search engine. Shorts (under 60 seconds) get pushed to a massive audience. Full-length videos also rank in Google search results.",
              },
              {
                platform: "LinkedIn",
                why: "Best for B2B content: industry thought leadership, recruitment, and connecting with other professionals. Property videos perform well here for premium and commercial listings.",
              },
            ].map((p) => (
              <div
                key={p.platform}
                className="rounded-xl border border-stone bg-white p-5"
              >
                <h3 className="font-display text-base font-bold text-charcoal">
                  {p.platform}
                </h3>
                <p className="mt-2 text-sm text-slate">{p.why}</p>
              </div>
            ))}
          </div>

          {/* Best practices */}
          <h2 className="mt-12 font-display text-2xl font-bold text-charcoal">
            Best practices<span className="text-red">.</span>
          </h2>
          <ul className="mt-4 space-y-2 text-sm text-slate">
            {[
              "Keep videos short: 15-45 seconds for social media, 60-90 seconds for websites",
              "Lead with the best feature: the first 3 seconds determine whether someone keeps watching",
              "Add captions and text overlays: 85% of social media video is watched without sound",
              "Include your branding: logo, colours, and agency name should be visible throughout",
              "Post consistently: 3-5 videos per week performs better than sporadic posting",
              "Use portrait (9:16) for social media and landscape (16:9) for websites",
              "Always include a call to action: \"Book a viewing\", \"Contact us\", or your phone number",
              "Repurpose one video across all platforms to maximise reach with minimal effort",
            ].map((tip) => (
              <li key={tip} className="flex gap-2">
                <span className="mt-0.5 text-red">&#8226;</span>
                <span>{tip}</span>
              </li>
            ))}
          </ul>

          {/* Getting started */}
          <h2 className="mt-12 font-display text-2xl font-bold text-charcoal">
            Getting started with video<span className="text-red">.</span>
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate">
            <p>
              You don&rsquo;t need expensive equipment or editing skills.
              With QuiteRight, the process is simple: paste a listing URL
              from Rightmove or Zoopla, choose a template and tone, and
              the platform creates a professional video ad in under 60
              seconds. It handles the script, voiceover, music, and
              rendering automatically.
            </p>
            <p>
              Start by creating videos for your best listings, then expand
              to every new instruction. Publish across Instagram, TikTok,
              Facebook, and YouTube directly from the platform. Track
              which videos drive the most enquiries and refine your
              approach.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-t border-stone bg-charcoal px-6 py-16">
        <ScrollReveal
          stagger
          className="mx-auto grid max-w-5xl grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate/20 bg-slate/20 sm:grid-cols-4"
        >
          {[
            { stat: "2x", label: "more enquiries on listings with video" },
            { stat: "5x", label: "more engagement than static photos" },
            { stat: "73%", label: "of buyers watch video before viewing" },
            { stat: "<60s", label: "to create a video with QuiteRight" },
          ].map((item) => (
            <div key={item.stat} className="bg-charcoal px-5 py-6 text-center">
              <p className="font-display text-2xl font-bold text-cream md:text-3xl">
                {item.stat}
              </p>
              <p className="mt-2 text-xs leading-snug text-warm-grey">{item.label}</p>
            </div>
          ))}
        </ScrollReveal>
      </section>

      {/* Related */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Related resources<span className="text-red">.</span>
          </h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {[
              { href: "/tools/listing-cost-calculator", title: "Listing cost calculator", desc: "Compare video costs: traditional vs QuiteRight." },
              { href: "/why-quiteright", title: "Why QuiteRight", desc: "The ROI of property video marketing for estate agents." },
            ].map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="group flex flex-col rounded-xl border border-stone bg-white p-5 transition-all hover:border-red/30 hover:shadow-sm"
              >
                <p className="font-display text-base font-bold text-charcoal transition-colors group-hover:text-red">
                  {item.title}
                </p>
                <p className="mt-1 flex-1 text-sm text-slate">{item.desc}</p>
                <p className="mt-3 flex items-center gap-1 text-xs font-medium text-red">
                  Explore
                  <svg viewBox="0 0 20 20" fill="currentColor" className="size-3.5 transition-transform group-hover:translate-x-0.5">
                    <path fillRule="evenodd" d="M5 10a.75.75 0 0 1 .75-.75h6.638L10.23 7.29a.75.75 0 1 1 1.04-1.08l3.5 3.25a.75.75 0 0 1 0 1.08l-3.5 3.25a.75.75 0 1 1-1.04-1.08l2.158-1.96H5.75A.75.75 0 0 1 5 10Z" clipRule="evenodd" />
                  </svg>
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal className="text-center">
            <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common questions<span className="text-red">.</span>
            </h2>
          </ScrollReveal>
          <div className="mt-12 divide-y divide-stone">
            {FAQ.map((item) => (
              <details key={item.q} className="group py-5">
                <summary className="flex cursor-pointer items-center justify-between text-base font-medium text-charcoal">
                  {item.q}
                  <svg viewBox="0 0 20 20" fill="currentColor" className="size-5 shrink-0 text-warm-grey transition-transform group-open:rotate-180">
                    <path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
                  </svg>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl lg:text-5xl">
            Start creating property videos today
            <span className="text-red">.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg text-warm-grey">
            No camera, no editing skills, no waiting. Paste a listing URL
            and get a scroll-stopping video ad in under 60 seconds.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/sign-up"
              className="inline-flex w-full items-center justify-center rounded-lg bg-red px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              Start your free trial
            </Link>
            <Link
              href="/examples"
              className="inline-flex w-full items-center justify-center rounded-lg border border-cream/20 px-7 py-3.5 text-base font-medium text-cream transition-colors hover:border-cream/40 hover:bg-cream/5 sm:w-auto"
            >
              See video examples
            </Link>
          </div>
          <p className="mt-5 text-sm text-warm-grey">
            14-day free trial &middot; No credit card &middot; Cancel any time
          </p>
        </div>
      </section>
    </PublicLayout>
  )
}
